/**
 * useAddPlaneWizard - Custom hook for orchestrating the Add Plane wizard
 *
 * Handles:
 * - Opening/closing the wizard
 * - Coordinating with MapboxScene for placement mode
 * - Submitting wizard data to create all entities
 */

import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'

import { useCreateClient } from '@/hooks/useClients'
import { useCreateContact } from '@/hooks/useContacts'
import { useCreateAircraft, useUpdateAircraft, aircraftKeys } from '@/hooks/useAircraft'
import { useCreateTransientStay, useUpdateTransientStay, transientStayKeys } from '@/hooks/useTransientStays'
import { useCreateLease, leaseKeys } from '@/hooks/useLeases'
import { useCreateAllocation, useUpdateAllocation, allocationKeys } from '@/hooks/useAllocations'
import { useTenant } from '@/contexts'

import {
  useAddPlaneWizardStore,
  generateTempTailNumber,
  type WizardSubmissionData,
} from '@/stores/addPlaneWizardStore'

interface UseAddPlaneWizardOptions {
  locationId?: string
  onSuccess?: (data: WizardSubmissionData) => void
  onError?: (error: Error) => void
}

export function useAddPlaneWizard({
  locationId: locationIdProp,
  onSuccess,
  onError,
}: UseAddPlaneWizardOptions) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id

  // Store actions
  const {
    isOpen,
    openWizard,
    closeWizard,
    reset,
    isPlacementModeActive,
    activatePlacementMode,
    cancelPlacement,
    confirmPlacement,
    setSubmitting,
    setSubmissionError,
    getWizardData,
  } = useAddPlaneWizardStore()

  // Mutations
  const createClient = useCreateClient()
  const createContact = useCreateContact()
  const createAircraft = useCreateAircraft()
  const updateAircraft = useUpdateAircraft()
  const createTransientStay = useCreateTransientStay()
  const updateTransientStay = useUpdateTransientStay()
  const createLease = useCreateLease()
  const createAllocation = useCreateAllocation()
  const updateAllocation = useUpdateAllocation()

  // Submit the wizard (creates all entities)
  const submit = useCallback(async () => {
    if (!organizationId) {
      toast.error('No organization selected')
      return
    }

    const data = getWizardData()
    const { wizardContext } = data

    // Resolve locationId: prop > wizard context > empty
    const locationId = locationIdProp || wizardContext.locationId || ''

    setSubmitting(true)
    setSubmissionError(null)

    try {
      // Resolve tail number (generate temp if needed)
      const effectiveTailNumber = data.noTailNumber
        ? generateTempTailNumber()
        : data.tailNumber

      if (wizardContext.source === 'complete_setup' && data.existingAllocationId) {
        // COMPLETE SETUP PATH: Client → Contract → Update existing allocation + aircraft
        const aircraftId = data.existingAircraftId!

        // Step 1: Create or get client + contact
        let clientId = data.clientId
        let contactId = data.contactId

        if (data.newClient && !clientId) {
          const newClient = await createClient.mutateAsync({
            client_type: data.newClient.client_type,
            company_name: data.newClient.company_name,
            first_name: data.newClient.first_name,
            last_name: data.newClient.last_name,
            email_primary: data.newClient.email_primary,
            phone_primary: data.newClient.phone_primary,
          })
          clientId = newClient.id

          if (data.newContact) {
            const newContact = await createContact.mutateAsync({
              client_id: clientId,
              organization_id: organizationId,
              first_name: data.newContact.first_name,
              last_name: data.newContact.last_name,
              email_primary: data.newContact.email_primary,
              phone_mobile: data.newContact.phone_mobile,
              is_primary_contact: true,
            })
            contactId = newContact.id
          }
        } else if (clientId && data.isCreatingNewContactForExistingClient && data.newContact) {
          const newContact = await createContact.mutateAsync({
            client_id: clientId,
            organization_id: organizationId,
            first_name: data.newContact.first_name,
            last_name: data.newContact.last_name,
            email_primary: data.newContact.email_primary,
            phone_mobile: data.newContact.phone_mobile,
            is_primary_contact: false,
          })
          contactId = newContact.id
        }

        // Step 2: Create contract (transient or lease)
        let transientStayId: string | undefined
        let leaseContractId: string | undefined

        if (data.contract.type === 'transient') {
          const transient = await createTransientStay.mutateAsync({
            aircraft_id: aircraftId,
            client_id: clientId || undefined,
            location_id: locationId,
            check_in: data.contract.check_in,
            expected_check_out: data.contract.expected_check_out,
            booking_type: data.contract.booking_type,
            source: 'customer_portal',
            status: 'confirmed',
            rate_type: data.contract.rate_type ?? undefined,
            rate_amount: data.contract.rate_amount ?? undefined,
          })
          transientStayId = transient.id
        } else {
          const lease = await createLease.mutateAsync({
            data: {
              location_id: locationId,
              client_id: clientId!,
              unit_id: data.unitId || undefined,
              type: data.contract.lease_type,
              start_date: data.contract.start_date,
              end_date: data.contract.end_date ?? undefined,
              monthly_rate: data.contract.monthly_rate,
              rate_basis: data.contract.rate_basis ?? undefined,
              security_deposit: data.contract.security_deposit ?? undefined,
              status: 'active',
              fuel_type: data.contract.fuel_type ?? undefined,
              fuel_commitment_gallons_monthly: data.contract.fuel_commitment_gallons_monthly ?? undefined,
              fuel_commitment_gallons_annual: data.contract.fuel_commitment_gallons_annual ?? undefined,
              fuel_price_per_gallon: data.contract.fuel_price_per_gallon ?? undefined,
              fuel_price_type: data.contract.fuel_price_type ?? undefined,
              fuel_price_differential: data.contract.fuel_price_differential ?? undefined,
            },
            aircraftIds: [aircraftId],
            primaryAircraftId: aircraftId,
          })
          leaseContractId = lease.id
        }

        // Step 3: Update existing allocation with contract reference
        const allocationUpdate: Record<string, unknown> = {}
        if (transientStayId) allocationUpdate.transient_stay_id = transientStayId
        if (leaseContractId) allocationUpdate.lease_contract_id = leaseContractId
        await updateAllocation.mutateAsync({
          id: data.existingAllocationId,
          data: allocationUpdate,
        })

        // Link allocation back to transient stay
        if (transientStayId) {
          await updateTransientStay.mutateAsync({
            id: transientStayId,
            data: { allocation_id: data.existingAllocationId },
          })
        }

        // Step 4: Update aircraft with owner + default contact
        const aircraftUpdate: Record<string, unknown> = {}
        if (clientId) aircraftUpdate.owner_client_id = clientId
        if (contactId) aircraftUpdate.default_contact_id = contactId
        if (Object.keys(aircraftUpdate).length > 0) {
          await updateAircraft.mutateAsync({
            id: aircraftId,
            data: aircraftUpdate,
          })
        }

        // Refetch queries for immediate data update
        await Promise.all([
          queryClient.refetchQueries({ queryKey: aircraftKeys.lists() }),
          queryClient.refetchQueries({ queryKey: allocationKeys.lists() }),
          queryClient.refetchQueries({ queryKey: transientStayKeys.lists() }),
          queryClient.refetchQueries({ queryKey: leaseKeys.lists() }),
          queryClient.refetchQueries({ queryKey: ['map-allocations'] }),
        ])

        toast.success(`Setup completed for ${effectiveTailNumber}`)
        onSuccess?.(data)
      } else if (data.isQuickPlaceMode) {
        // QUICK-PLACE PATH: Aircraft + Allocation only (no client, no contract)
        let aircraft: { id: string }

        if (data.isCreatingNewAircraft) {
          aircraft = await createAircraft.mutateAsync({
            aircraft_type_id: data.aircraftTypeId!,
            tail_number: effectiveTailNumber,
            home_location_id: locationId || undefined,
            current_location_id: locationId || undefined,
          })
        } else {
          aircraft = { id: data.existingAircraftId! }
        }

        // Create allocation at placed position
        await createAllocation.mutateAsync({
          aircraft_id: aircraft.id,
          location_id: locationId,
          area_id: data.areaId || undefined,
          unit_id: data.unitId || undefined,
          position_x: data.position?.x,
          position_y: data.position?.y,
          position_z: data.position?.z,
          rotation: data.rotation,
          position_type: data.unitId ? 'hangar' : 'ramp',
          status: 'active',
          presence: data.presenceStatus,
          starts_at: data.contract?.type === 'transient' && data.contract.check_in
            ? new Date(data.contract.check_in).toISOString()
            : data.contract?.type === 'long_term' && data.contract.start_date
              ? new Date(data.contract.start_date).toISOString()
              : new Date().toISOString(),
        })

        // Refetch relevant queries
        await Promise.all([
          queryClient.refetchQueries({ queryKey: aircraftKeys.lists() }),
          queryClient.refetchQueries({ queryKey: allocationKeys.lists() }),
          queryClient.refetchQueries({ queryKey: ['map-allocations'] }),
        ])

        toast.success(`Aircraft ${effectiveTailNumber} placed on map`)
        onSuccess?.(data)
      } else {
        // FULL PATH: Client → Aircraft → Contract → (optional) Allocation

        // Should we create an allocation? Only when:
        // 1. Full path with placement method (user placed on map), OR
        // 2. Full path from ramp (always creates allocation)
        const shouldCreateAllocation =
          data.locationMethod === 'placement' ||
          wizardContext.source === 'ramp'

        // Step 1: Create or get client
        let clientId = data.clientId
        let contactId = data.contactId

        if (data.newClient && !clientId) {
          const newClient = await createClient.mutateAsync({
            client_type: data.newClient.client_type,
            company_name: data.newClient.company_name,
            first_name: data.newClient.first_name,
            last_name: data.newClient.last_name,
            email_primary: data.newClient.email_primary,
            phone_primary: data.newClient.phone_primary,
          })
          clientId = newClient.id

          if (data.newContact) {
            const newContact = await createContact.mutateAsync({
              client_id: clientId,
              organization_id: organizationId,
              first_name: data.newContact.first_name,
              last_name: data.newContact.last_name,
              email_primary: data.newContact.email_primary,
              phone_mobile: data.newContact.phone_mobile,
              is_primary_contact: true,
            })
            contactId = newContact.id
          }
        } else if (clientId && data.isCreatingNewContactForExistingClient && data.newContact) {
          const newContact = await createContact.mutateAsync({
            client_id: clientId,
            organization_id: organizationId,
            first_name: data.newContact.first_name,
            last_name: data.newContact.last_name,
            email_primary: data.newContact.email_primary,
            phone_mobile: data.newContact.phone_mobile,
            is_primary_contact: false,
          })
          contactId = newContact.id
        }

        // Step 2: Create or use existing aircraft
        let aircraft: { id: string }

        if (data.isCreatingNewAircraft) {
          aircraft = await createAircraft.mutateAsync({
            aircraft_type_id: data.aircraftTypeId!,
            tail_number: effectiveTailNumber,
            owner_client_id: clientId || undefined,
            default_contact_id: contactId || undefined,
            home_location_id: locationId || undefined,
            current_location_id: locationId || undefined,
          })
        } else {
          aircraft = { id: data.existingAircraftId! }
          // Update existing aircraft with default_contact_id if contact was selected/created
          if (contactId) {
            await updateAircraft.mutateAsync({
              id: aircraft.id,
              data: { default_contact_id: contactId },
            })
          }
        }

        // Step 3: Create contract (transient or lease)
        let transientStayId: string | undefined
        let leaseContractId: string | undefined

        if (data.linkedLeaseContractId) {
          // Owner already holds an active lease here — link to it, create no new contract.
          leaseContractId = data.linkedLeaseContractId
        } else if (data.contract.type === 'transient') {
          const transient = await createTransientStay.mutateAsync({
            aircraft_id: aircraft.id,
            client_id: clientId || undefined,
            location_id: locationId,
            unit_id: data.unitId || undefined,
            check_in: data.contract.check_in,
            expected_check_out: data.contract.expected_check_out,
            booking_type: data.contract.booking_type,
            source: 'customer_portal',
            status: 'confirmed',
            rate_type: data.contract.rate_type ?? undefined,
            rate_amount: data.contract.rate_amount ?? undefined,
          })
          transientStayId = transient.id
        } else {
          const lease = await createLease.mutateAsync({
            data: {
              location_id: locationId,
              client_id: clientId!,
              unit_id: data.unitId!,
              type: data.contract.lease_type,
              start_date: data.contract.start_date,
              end_date: data.contract.end_date ?? undefined,
              monthly_rate: data.contract.monthly_rate,
              rate_basis: data.contract.rate_basis ?? undefined,
              security_deposit: data.contract.security_deposit ?? undefined,
              status: 'active',
              fuel_type: data.contract.fuel_type ?? undefined,
              fuel_commitment_gallons_monthly: data.contract.fuel_commitment_gallons_monthly ?? undefined,
              fuel_commitment_gallons_annual: data.contract.fuel_commitment_gallons_annual ?? undefined,
              fuel_price_per_gallon: data.contract.fuel_price_per_gallon ?? undefined,
              fuel_price_type: data.contract.fuel_price_type ?? undefined,
              fuel_price_differential: data.contract.fuel_price_differential ?? undefined,
            },
            aircraftIds: [aircraft.id],
            primaryAircraftId: aircraft.id,
          })
          leaseContractId = lease.id
        }

        // Step 4: Conditionally create allocation
        if (shouldCreateAllocation && locationId) {
          const allocation = await createAllocation.mutateAsync({
            aircraft_id: aircraft.id,
            location_id: locationId,
            area_id: data.areaId || undefined,
            unit_id: data.unitId || undefined,
            transient_stay_id: transientStayId,
            lease_contract_id: leaseContractId,
            position_x: data.position?.x,
            position_y: data.position?.y,
            position_z: data.position?.z,
            rotation: data.rotation,
            position_type: data.unitId ? 'hangar' : 'ramp',
            status: 'active',
            presence: data.presenceStatus,
            starts_at: data.contract?.type === 'transient' && data.contract.check_in
              ? new Date(data.contract.check_in).toISOString()
              : data.contract?.type === 'long_term' && data.contract.start_date
                ? new Date(data.contract.start_date).toISOString()
                : new Date().toISOString(),
          })

          // Link allocation back to transient stay
          if (transientStayId) {
            await updateTransientStay.mutateAsync({
              id: transientStayId,
              data: { allocation_id: allocation.id },
            })
          }
        }

        // Refetch queries for immediate data update
        await Promise.all([
          queryClient.refetchQueries({ queryKey: aircraftKeys.lists() }),
          queryClient.refetchQueries({ queryKey: allocationKeys.lists() }),
          queryClient.refetchQueries({ queryKey: transientStayKeys.lists() }),
          queryClient.refetchQueries({ queryKey: leaseKeys.lists() }),
          queryClient.refetchQueries({ queryKey: ['map-allocations'] }),
        ])

        // Source-specific toast
        const toastMsg =
          wizardContext.source === 'transient'
            ? `Booking created for ${effectiveTailNumber}`
            : wizardContext.source === 'lease'
              ? `Lease created for ${effectiveTailNumber}`
              : `Aircraft ${effectiveTailNumber} added successfully`
        toast.success(toastMsg)
        onSuccess?.(data)
      }

      // Reset and close wizard
      const returnPath = wizardContext.returnPath
      reset()
      closeWizard()

      // Navigate back if opened from non-ramp page
      if (returnPath) {
        navigate(returnPath)
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Unknown error')
      setSubmissionError(err.message)
      toast.error('Failed to add aircraft', {
        description: err.message,
      })
      onError?.(err)
    } finally {
      setSubmitting(false)
    }
  }, [
    organizationId,
    getWizardData,
    setSubmitting,
    setSubmissionError,
    createClient,
    createContact,
    createAircraft,
    createTransientStay,
    updateTransientStay,
    createLease,
    createAllocation,
    locationIdProp,
    queryClient,
    onSuccess,
    reset,
    closeWizard,
    onError,
    navigate,
  ])

  // Handle placement mode start
  const startPlacementMode = useCallback(() => {
    activatePlacementMode()
  }, [activatePlacementMode])

  // Handle placement mode cancel
  const cancelPlacementMode = useCallback(() => {
    cancelPlacement()
  }, [cancelPlacement])

  // Handle placement mode confirm
  const confirmPlacementMode = useCallback(() => {
    confirmPlacement()
    // Automatically submit after placement
    submit()
  }, [confirmPlacement, submit])

  return {
    // State
    isOpen,
    isPlacementModeActive,

    // Actions
    openWizard,
    closeWizard,
    reset,
    submit,

    // Placement mode
    startPlacementMode,
    cancelPlacementMode,
    confirmPlacementMode,

    // Loading state
    isSubmitting:
      createClient.isPending ||
      createContact.isPending ||
      createAircraft.isPending ||
      updateAircraft.isPending ||
      createTransientStay.isPending ||
      updateTransientStay.isPending ||
      createLease.isPending ||
      createAllocation.isPending ||
      updateAllocation.isPending,
  }
}

export default useAddPlaneWizard
